import { useCallback, useEffect, useRef, useState } from 'react'
import './drawn-frame.css'

const SCHEME_FEATURE = /\(\s*prefers-color-scheme\s*:\s*(light|dark)\s*\)/g

/**
 * Answer a drawing's `prefers-color-scheme` queries as one scheme. A frame's
 * document answers them with the reader's own preference whatever the frame
 * element declares, so the rules are rewritten in place: a matching feature
 * becomes one always true, the other one never true.
 */
function fixScheme(doc: Document, scheme: 'light' | 'dark') {
  doc.documentElement.style.colorScheme = scheme
  /* The rules belong to the frame's window, so they are tested against its
     constructors rather than the page's. */
  const frameWindow = doc.defaultView
  if (!frameWindow) return
  const visit = (rules: CSSRuleList) => {
    for (const rule of Array.from(rules)) {
      if (rule instanceof frameWindow.CSSMediaRule) {
        const text = rule.media.mediaText
        if (text.includes('prefers-color-scheme')) {
          rule.media.mediaText = text.replace(SCHEME_FEATURE, (_, wanted) =>
            wanted === scheme ? '(min-width: 0px)' : '(max-width: -1px)',
          )
        }
      }
      if (rule instanceof frameWindow.CSSGroupingRule) visit(rule.cssRules)
    }
  }
  for (const sheet of Array.from(doc.styleSheets)) visit(sheet.cssRules)
}

/**
 * A document somebody drew, held in a frame of its own and sized to it.
 *
 * The frame keeps the drawing's styles off the page and the page's off the
 * drawing, and runs no script: the sandbox grants the same origin only so the
 * page can read the drawing's height, which a frame cannot report by itself.
 * Its media queries answer the frame's width, which is the column's measure.
 *
 * The height follows the drawing's own box rather than the frame's, so a face
 * or an image settling after load grows the frame with it.
 *
 * `scheme` fixes the color scheme the drawing answers to, so a preview can
 * show both; without it the drawing follows the reader's.
 */
export function DrawnFrame({
  html,
  title,
  scheme,
}: {
  html: string
  title: string
  scheme?: 'light' | 'dark'
}) {
  const frame = useRef<HTMLIFrameElement>(null)
  const watch = useRef<ResizeObserver | null>(null)
  const [height, setHeight] = useState<number | null>(null)

  const follow = useCallback(() => {
    const doc = frame.current?.contentDocument
    const root = doc?.documentElement
    if (!doc || !root) return
    if (scheme) fixScheme(doc, scheme)
    watch.current?.disconnect()
    /* The root's own box, not its scroll height: a scroll height is never
       less than the frame, so a drawing that narrows taller and back would
       leave the frame at its tallest. */
    const measure = () =>
      setHeight(Math.ceil(root.getBoundingClientRect().height))
    watch.current = new ResizeObserver(measure)
    watch.current.observe(root)
    measure()
  }, [scheme])

  useEffect(() => () => watch.current?.disconnect(), [])

  return (
    <iframe
      ref={frame}
      className="drawn-frame"
      title={title}
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={follow}
      style={height === null ? undefined : { height }}
    />
  )
}
